import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

/**
 * 手動決済確認ダイアログ
 * API連携のない決済端末（Airpay, JMS, USEN PAY等）で使用。
 * スタッフに金額を表示し、外部端末での処理完了を確認させる。
 */
const ManualPaymentDialog = ({
  open,
  onClose,
  onConfirm,
  amount,
  paymentMethodName,
  formatCurrency,
}) => {
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm({ reference, note });
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (!confirming) {
      setReference('');
      setNote('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <CreditCardIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
        <Typography variant="h6" component="div">
          外部端末で決済してください
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
        {/* 決済方法 */}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          決済方法: {paymentMethodName}
        </Typography>

        {/* 金額を大きく表示 */}
        <Box sx={{
          my: 2,
          p: 3,
          bgcolor: 'warning.light',
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'warning.main',
        }}>
          <Typography variant="h3" fontWeight={700} color="warning.dark">
            {formatCurrency(amount)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          決済端末でこの金額を処理した後、<br />
          下の「決済完了」を押してください
        </Typography>

        {/* 任意の参照情報 */}
        <TextField
          fullWidth
          size="small"
          label="端末トランザクションID（任意）"
          placeholder="端末に表示されたIDを入力"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          margin="dense"
          sx={{ mt: 1 }}
        />
        <TextField
          fullWidth
          size="small"
          label="メモ（任意）"
          placeholder="備考があれば入力"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          margin="dense"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          onClick={handleConfirm}
          disabled={confirming}
          startIcon={confirming ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700 }}
        >
          {confirming ? '処理中...' : '決済完了'}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          fullWidth
          onClick={handleClose}
          disabled={confirming}
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualPaymentDialog;
